import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";

import { AliasEditorComponent } from "../../../../components/common/alias-editor/alias-editor.component";
import { TrafficLineChart } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.component";
import { TrafficSubject } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.helpers";
import { L3vpnDetailContainer } from "../../../../components/subscription-detail/l3vpn-detail-container/l3vpn-detail-container";
import { Networkgraph } from "../../../../components/subscription-detail/networkgraph/networkgraph";
import { PageTabComponent } from "../../../../components/subscription-detail/page-tab/page-tab";
import { ServiceId } from "../../../../components/subscription-detail/service-id/service-id";
import { SubscriptionDetailHeader } from "../../../../components/subscription-detail/subscription-detail-header/subscription-detail-header";
import { ThirdPartyServiceIdsRowContentComponent } from "../../../../components/subscription-detail/third-party-service-ids-row-content/third-party-service-ids-row-content";
import { EnabledPipe } from "../../../../helpers/enabledpipe/enabledpipe";
import { ServiceSpeedPipe } from "../../../../helpers/service-speed-pipe/service-speed-pipe";
import { SubscriptionDatePipe } from "../../../../helpers/subscription-datepipe/subscription-datepipe";
import { L3vpnSubscriptionDetails } from "../../types";
import { SubscriptionViewBase } from "../subscription-view-base";
import { L3vpnShortCutMenu } from "./components/l3vpn-shortcut-menu/l3vpn-shortcut-menu";

@Component({
  selector: "l3vpn-subscription-view",
  templateUrl: "l3vpn-subscription-view.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AliasEditorComponent,
    EnabledPipe,
    L3vpnShortCutMenu,
    Networkgraph,
    PageTabComponent,
    ServiceId,
    ServiceSpeedPipe,
    SubscriptionDatePipe,
    SubscriptionDetailHeader,
    L3vpnDetailContainer,
    ThirdPartyServiceIdsRowContentComponent,
    TrafficLineChart,
    TranslatePipe,
  ],
})
export class L3vpnSubscriptionView extends SubscriptionViewBase<L3vpnSubscriptionDetails> {
  protected speedPolicer = computed(() => this.subscription().speedPolicer);
  protected l3vpnAsn = computed(() => this.subscription().l3vpnss?.l3vpnAsn);
  protected topology = computed(() => this.subscription().topology);
  protected domain = computed(() => this.subscription().domain);
  protected protection = computed(() => this.subscription().protection);
  protected serviceSpeed = computed(() => this.subscription().serviceSpeed);

  private translate = inject(TranslateService);
  protected chartSubjects = computed<TrafficSubject[]>(() =>
    buildL3vpnChartSubjects(this.subscription(), this.translate),
  );
}

export function buildL3vpnChartSubjects(
  subscription: L3vpnSubscriptionDetails,
  translate: TranslateService,
): TrafficSubject[] {
  const firewallInterconnectTitle = translate.instant("ServiceElement.FirewallInterconnect") as unknown as string;
  const portTitle = translate.instant("ServiceElement.Port") as unknown as string;

  const firewallItems = subscription.firewallInterconnects.map((fwInterconnect) => ({
    subscriptionId: subscription.subscriptionId,
    subscriptionInstanceIds: fwInterconnect.saps.map((sap) => sap.subscriptionInstanceId),
    name: firewallInterconnectTitle,
  }));

  const portItems = subscription.l3vpnsapss
    .filter((endpoint) => !!endpoint.sap.port)
    .map((endpoint, index) => ({
      subscriptionId: subscription.subscriptionId,
      subscriptionInstanceIds: [endpoint.sap.subscriptionInstanceId],
      name: `${portTitle} ${index + 1} - ${endpoint.sap.port?.description}`,
    }));

  const allPortsItem = {
    subscriptionId: subscription.subscriptionId,
    subscriptionInstanceIds: [...firewallItems, ...portItems].flatMap((item) => item.subscriptionInstanceIds ?? []),
    name: translate.instant("Chart.AllPorts") as unknown as string,
  };

  return [allPortsItem, ...firewallItems, ...portItems];
}
